/**
 * MOTOR DE SINCRONIZACIÓN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LA REGLA QUE MANDA AQUÍ: NUNCA SE PIERDE UNA VENTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una venta en la cola es dinero que un cliente ya entregó. Solo puede salir de
 * la cola por dos caminos:
 *
 *   1. El servidor la aceptó  → se borra (ya está guardada allá).
 *   2. El servidor la rechazó → pasa a la BANDEJA DE CONFLICTOS con el motivo,
 *                               para que una persona decida qué hacer.
 *
 * Un fallo de red NO es ninguno de los dos: la venta se queda en la cola y se
 * reintenta. Esa distinción es todo el módulo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ REENVIAR DOS VECES NO DUPLICA NADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cada venta lleva un `client_uuid` generado por el dispositivo. Si se envía, la
 * conexión se corta antes de recibir la respuesta y se reintenta, el servidor
 * reconoce ese identificador y devuelve la venta que ya tenía en vez de crear
 * otra. Por eso podemos reintentar sin miedo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LOS CLIENTES VAN PRIMERO. SIEMPRE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sin señal, el cajero registra a la señora que tiene delante y le fía. Se
 * guardan dos cosas: el cliente y la venta que lo nombra. Mandando la venta
 * primero, el servidor contestaría «ese cliente no existe» y el fiado acabaría
 * en la bandeja de conflictos en vez de en la cuenta de doña Carmen.
 *
 * Y hay un caso en que el cliente cambia de id al llegar: que esa cédula ya
 * estuviera registrada. El servidor devuelve entonces el cliente de siempre
 * —con su propio id— y aquí se corrigen las ventas que apuntaban al nuevo antes
 * de mandarlas. Ver `reemplazarClienteEnLaCola`.
 */

import { create } from "zustand";
import { mensajeDeError, SoftfiError } from "@/lib/api";
import {
  clienteAceptado,
  clientesPendientes,
  contarClientesPendientes,
  contarConflictos,
  contarPendientes,
  descartarClientePendiente,
  marcarClienteFallido,
  marcarIntentoFallido,
  moverAConflicto,
  quitarPendiente,
  reemplazarClienteEnLaCola,
  ventasPendientes,
} from "@/lib/db";
import { clientes as clientesApi, pos } from "@/lib/endpoints";
import { aviso } from "./ui";

/** Cuántas ventas se envían por petición. */
const TAMANO_LOTE = 50;

/**
 * ¿El servidor lo rechazó, o es que no llegamos hasta él?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTA DISTINCIÓN ES TODO EL MOTOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un rechazo se va a repetir igual mil veces: el tipo de documento no existe en
 * ese país, el nombre está vacío. Reintentarlo es dejarlo dando vueltas para
 * siempre en una cola que nadie mira.
 *
 * Un corte de red no es un rechazo: no se llegó a preguntar. Eso se reintenta,
 * y es lo que hace que una venta guardada en el celular acabe llegando cuando
 * vuelve la señal.
 *
 * Se mira el estado HTTP: un 4xx es el servidor contestando que no. El 401 y el
 * 408 quedan fuera a propósito —sesión caducada y tiempo agotado son cosas que
 * SÍ se arreglan solas al reintentar— y el 429 también: «vas muy rápido» es
 * literalmente una petición de que se vuelva a intentar más tarde.
 */
function loRechazoElServidor(error: unknown): boolean {
  if (!(error instanceof SoftfiError)) return false;
  // ⚠️ «negocio_cambiado» no llegó al servidor: la pestaña se está poniendo al
  // día con el negocio de la sesión (ver `negocioEsperado` en `lib/api.ts`).
  // Tratarlo como rechazo descartaría clientes registrados sin internet.
  if (error.code === "negocio_cambiado") return false;
  if ([401, 408, 429].includes(error.status)) return false;
  return error.status >= 400 && error.status < 500;
}

type EstadoOffline = {
  pendientes: number;
  /** Clientes registrados aquí que todavía no han llegado al servidor. */
  clientes: number;
  conflictos: number;
  sincronizando: boolean;
  ultimaSync: number | null;

  refrescarContadores: () => Promise<void>;
  /** Envía la cola. `silencioso` evita los avisos cuando corre sola de fondo. */
  sincronizar: (opciones?: { silencioso?: boolean }) => Promise<void>;
};

export const useOffline = create<EstadoOffline>((set, get) => ({
  pendientes: 0,
  clientes: 0,
  conflictos: 0,
  sincronizando: false,
  ultimaSync: null,

  refrescarContadores: async () => {
    const [pendientes, clientes, conflictos] = await Promise.all([
      contarPendientes(),
      contarClientesPendientes(),
      contarConflictos(),
    ]);
    set({ pendientes, clientes, conflictos });
  },

  sincronizar: async ({ silencioso = false } = {}) => {
    if (get().sincronizando) return;

    const cola = await ventasPendientes();
    const clientesEnCola = await clientesPendientes();
    if (cola.length === 0 && clientesEnCola.length === 0) {
      await get().refrescarContadores();
      return;
    }

    set({ sincronizando: true });

    /*
      ⚠️ PRIMERO LOS CLIENTES, Y SI FALLAN NO SE PARA.

      Un cliente que no llega no puede bloquear las ventas de todo el día: las
      demás ventas —las que no son suyas— tienen que salir igual. La suya se
      quedará en la cola, se reintentará, y si el servidor la rechaza de plano
      acabará en conflictos con el motivo. Nada se pierde por el camino.
    */
    for (const cliente of clientesEnCola) {
      try {
        const real = await clientesApi.crear(cliente.datos);
        if (real.id === cliente.id) {
          await clienteAceptado(real);
        } else {
          // Esa cédula ya estaba registrada: el servidor devolvió al de
          // siempre. Las ventas guardadas pasan a nombrarlo a él.
          await reemplazarClienteEnLaCola(cliente.id, real);
        }
      } catch (error) {
        if (loRechazoElServidor(error)) {
          /*
            El servidor lo rechazó por lo que trae dentro, no por la red.
            Reintentarlo mil veces daría mil veces el mismo error, así que se
            descarta con un aviso — y con él la ficha local, para que nadie le
            venda a un cliente que no va a existir nunca.
          */
          await descartarClientePendiente(cliente.id);
          if (!silencioso) {
            aviso.error(
              `No se pudo registrar a ${cliente.datos.name}`,
              mensajeDeError(error),
            );
          }
        } else {
          await marcarClienteFallido(cliente.id, mensajeDeError(error));
        }
      }
    }

    // Las ventas pueden haber cambiado de cliente ahí arriba: se vuelven a leer.
    const ventas = cola.length > 0 ? await ventasPendientes() : [];

    let aceptadas = 0;
    let rechazadas = 0;
    let corteDeRed = false;
    /** Lo que dijo el servidor si rechazó el lote ENTERO (p. ej. la prueba venció). */
    let noDelServidor: string | null = null;

    try {
      for (let i = 0; i < ventas.length; i += TAMANO_LOTE) {
        const lote = ventas.slice(i, i + TAMANO_LOTE);

        let respuesta: Awaited<ReturnType<typeof pos.sincronizar>>;
        try {
          respuesta = await pos.sincronizar(lote.map((v) => v.payload));
        } catch (error) {
          // No llegamos al servidor. Las ventas SE QUEDAN en la cola: no fueron
          // rechazadas, solo no pudimos preguntar. Anotamos el intento y paramos:
          // si este lote no salió, los siguientes tampoco van a salir.
          const motivo = mensajeDeError(error);
          for (const v of lote) await marcarIntentoFallido(v.client_uuid, motivo);
          /*
            ⚠️ …O SÍ LLEGAMOS, Y DIJO QUE NO A TODO EL LOTE.

            Pasa cuando el problema no es de una venta sino de la cuenta: la
            prueba gratis se venció, la sesión no tiene permiso. El aviso decía
            «No se pudo conectar» y el dueño revisaba el wifi. Lo encontró la
            auditoría.

            Las ventas se quedan en la cola igual —no son malas, es la cuenta la
            que no puede recibirlas ahora—, pero se dice lo que contestó el
            servidor, que es lo único que le sirve para arreglarlo.
          */
          if (loRechazoElServidor(error)) noDelServidor = motivo;
          else corteDeRed = true;
          break;
        }

        for (const resultado of respuesta.results) {
          const venta = lote.find((v) => v.client_uuid === resultado.client_uuid);
          if (!venta) continue;

          if (resultado.ok) {
            // Aceptada (o reconocida como duplicada, que es igual de bueno).
            await quitarPendiente(venta.client_uuid);
            aceptadas++;
          } else {
            await moverAConflicto(venta, {
              code: resultado.error_code ?? "unknown_error",
              message:
                resultado.error_message ??
                "El servidor rechazó esta venta y no explicó por qué.",
            });
            rechazadas++;
          }
        }
      }
    } finally {
      set({ sincronizando: false, ultimaSync: Date.now() });
      await get().refrescarContadores();
    }

    if (silencioso) return;

    if (noDelServidor) {
      const quedan = get().pendientes;
      aviso.error(
        "El servidor no aceptó las ventas guardadas",
        `${noDelServidor} Siguen en este aparato (${quedan === 1 ? "1 venta" : `${quedan} ventas`}): no se pierde ninguna.`,
      );
      return;
    }

    if (corteDeRed) {
      aviso.error(
        "No se pudo conectar",
        `Quedan ${get().pendientes} venta(s) guardadas. Se enviarán solas.`,
      );
      return;
    }

    if (rechazadas > 0) {
      aviso.error(
        `${rechazadas} venta(s) necesitan tu atención`,
        "Revísalas en Ventas sin enviar. Ninguna se perdió.",
      );
    }

    if (aceptadas > 0) {
      aviso.exito(
        aceptadas === 1 ? "Se envió 1 venta" : `Se enviaron ${aceptadas} ventas`,
        "Ya están guardadas en el servidor.",
      );
    }
  },
}));
